import React from 'react';
import { ChevronDown, ChevronUp, Loader2, LogOut, RotateCcw, Unplug } from 'lucide-react';
import { cn } from '../../../utils/cn';

export interface StrategyActionToolbarProps {
  retryableCount: number;
  exitableCount: number;
  onRetryAll?: () => void;
  onExitAll?: () => void;
  /** Platform-only "stop watching" for the whole scope (strategy or basket).
   *  Count is confirmed in the dialog, so no count is passed here. */
  onTerminateAll?: () => void;
  /** When true, the Retry / Exit / Terminate buttons are disabled and show a spinner. */
  pending?: boolean;
  /** Pro-only: compact/expand toggle for the leg cards. */
  compact?: boolean;
  onToggleCompact?: () => void;
}

/**
 * Retry / exit bulk-action toolbar rendered between the header and body of
 * the strategy modal. Used by both Classic and Pro strategy modals.
 *
 * Renders whenever at least one control is visible — a Retry button, an
 * Exit button, or the Pro-only compact toggle. When nothing would render,
 * returns `null` so the modal collapses back to header + body.
 */
export const StrategyActionToolbar: React.FC<StrategyActionToolbarProps> = ({
  retryableCount, exitableCount, onRetryAll, onExitAll, onTerminateAll, pending = false,
  compact, onToggleCompact,
}) => {
  const hasRetryButton = retryableCount > 0 && !!onRetryAll;
  const hasExitButton = exitableCount > 0 && !!onExitAll;
  // Terminate is platform-only (any non-terminal leg) — the dialog's dry_run is
  // the authoritative count, so it renders whenever the handler is wired.
  const hasTerminateButton = !!onTerminateAll;
  const hasCompactToggle = !!onToggleCompact;

  if (!hasRetryButton && !hasExitButton && !hasTerminateButton && !hasCompactToggle) return null;

  const attentionTotal =
    (hasRetryButton ? retryableCount : 0) + (hasExitButton ? exitableCount : 0);

  return (
    <div className="px-5 py-2.5 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2 flex-wrap">
      {attentionTotal > 0 && (
        <span className="text-xs text-gray-600 dark:text-gray-300">
          {attentionTotal} position{attentionTotal === 1 ? '' : 's'} need{attentionTotal === 1 ? 's' : ''} attention
        </span>
      )}
      <div className="ml-auto flex items-center gap-2">
        {hasRetryButton && (
          <button
            type="button"
            onClick={onRetryAll}
            disabled={pending}
            className={cn(
              'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-orange-600 rounded transition-opacity',
              pending ? 'opacity-60 cursor-wait' : 'hover:bg-orange-700',
            )}
          >
            {pending
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <RotateCcw className="w-3 h-3" />}
            Retry {retryableCount}
          </button>
        )}
        {hasExitButton && (
          <button
            type="button"
            onClick={onExitAll}
            disabled={pending}
            className={cn(
              'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-red-600 rounded transition-opacity',
              pending ? 'opacity-60 cursor-wait' : 'hover:bg-red-700',
            )}
          >
            {pending
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <LogOut className="w-3 h-3" />}
            Exit {exitableCount}
          </button>
        )}
        {hasTerminateButton && (
          <button
            type="button"
            onClick={onTerminateAll}
            disabled={pending}
            title="Stop the platform from managing every position in this scope (does not touch the broker)"
            className={cn(
              'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 rounded transition-opacity',
              pending ? 'opacity-60 cursor-wait' : 'hover:bg-slate-200 dark:hover:bg-slate-600',
            )}
          >
            {pending
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Unplug className="w-3 h-3" />}
            Terminate
          </button>
        )}
        {hasCompactToggle && (
          <button
            type="button"
            onClick={onToggleCompact}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600"
            title={compact ? 'Switch to spacious cards' : 'Switch to compact view'}
          >
            {compact ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
            {compact ? 'Expand' : 'Compact'}
          </button>
        )}
      </div>
    </div>
  );
};

export default StrategyActionToolbar;
